'use client'

import { type FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { SubscriptionTypeV1 } from '@redteclab/api/clients/bully'

import {
  Accordion,
  AccordionDetails,
  AccordionStack,
  AccordionSummary,
  Button,
} from 'base-ui'

import { useGlobalConfigContext } from '../../global-config'
import { useApiSwrNewsletterSubscribeRequestMutation } from '../api/useNewsletterSubscribeRequestMutation'
import { SUBSCRIPTION_REGISTRATION_CHANNEL_HOME_ONE } from '../const'
import { useNewsletterSubscribeContext } from '../context/NewsletterSubscribeContext'

interface NewsletterSubscribeFormState {
  setHasError: (hasError: boolean) => void
  setHasResend: (hasResend: boolean) => void
}

export const NewsletterSubscribeFaq: FC<NewsletterSubscribeFormState> = ({
  setHasError,
  setHasResend,
}) => {
  const { language } = useGlobalConfigContext()

  const { email, setEmail } = useNewsletterSubscribeContext()

  const newsletterSubscribeSwr = useApiSwrNewsletterSubscribeRequestMutation()

  const [voucherExpanded, setVoucherExpanded] = useState(false)
  const [noMailExpanded, setNoMailExpanded] = useState(false)

  const resendEmail = async (): Promise<void> => {
    if (newsletterSubscribeSwr.isMutating) {
      return
    }
    try {
      await newsletterSubscribeSwr.trigger({
        emailAddress: email,
        language,
        subscriptionRegistrationChannel:
          SUBSCRIPTION_REGISTRATION_CHANNEL_HOME_ONE,
        subscriptionType: SubscriptionTypeV1.Newsletter,
      })
      setEmail(email)
      setHasError(false)
      setHasResend(true)
    } catch {
      setEmail('')
      setHasError(true)
      setHasResend(false)
    } finally {
      // Close both accordions after attempting to resend
      setVoucherExpanded(false)
      setNoMailExpanded(false)
    }
  }

  return (
    <div className="pt-5">
      <h1 className="mb-4 text-m font-medium">
        <FormattedMessage id="newsletter.faq.headline" />
      </h1>
      <AccordionStack>
        <Accordion
          defaultExpanded={false}
          expanded={voucherExpanded}
          onChange={(_event, expanded) => {
            setVoucherExpanded(expanded)
          }}
        >
          <AccordionSummary className="bg-transparent">
            <FormattedMessage id="newsletter.subscribe.optInResult.faq.voucher" />
          </AccordionSummary>
          <AccordionDetails>
            <FormattedMessage
              id="newsletter.subscribe.optIn.faq.voucher.message"
              values={{ email }}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          defaultExpanded={false}
          expanded={noMailExpanded}
          onChange={(_event, expanded) => {
            setNoMailExpanded(expanded)
          }}
        >
          <AccordionSummary className="bg-transparent">
            <FormattedMessage id="newsletter.subscribe.optInSend.faq.noOptInMailReceived" />
          </AccordionSummary>
          <AccordionDetails>
            <p className="mb-4">
              <FormattedMessage id="newsletter.subscribe.optInSend.faq.resendEmail" />
            </p>
            <Button
              className="w-full"
              color="primary"
              data-qa-id="form-resend"
              onClick={() => {
                void resendEmail()
              }}
              variant="outlined"
            >
              <FormattedMessage id="newsletter.subscribe.optInSend.faq.resendEmail.button" />
            </Button>
          </AccordionDetails>
        </Accordion>
      </AccordionStack>
    </div>
  )
}
